import type { SignUpResource } from "@clerk/shared/types"

export type InvitationProfileField = "first_name" | "last_name"

type InvitationSignUpAttempt = Pick<
  SignUpResource,
  | "createdSessionId"
  | "emailAddress"
  | "missingFields"
  | "requiredFields"
  | "status"
  | "unverifiedFields"
>

type InvitationSignUpNextStep =
  | { kind: "complete"; sessionId: string }
  | { fields: InvitationProfileField[]; kind: "collect_profile" }
  | { kind: "collect_email" }
  | { emailAddress: string | null; kind: "enterprise_sso" }
  | { emailAddress: string; kind: "verify_email" }
  | { kind: "abandoned" }
  | { kind: "unsupported" }

function isInvitationProfileField(
  field: string,
): field is InvitationProfileField {
  return field === "first_name" || field === "last_name"
}

export function getInvitationSignUpNextStep(
  attempt: InvitationSignUpAttempt,
): InvitationSignUpNextStep {
  if (attempt.status === "complete" && attempt.createdSessionId) {
    return { kind: "complete", sessionId: attempt.createdSessionId }
  }

  if (attempt.status === "abandoned") {
    return { kind: "abandoned" }
  }

  const profileFields = attempt.missingFields.filter(isInvitationProfileField)
  if (profileFields.length > 0) {
    return { kind: "collect_profile", fields: profileFields }
  }

  if (attempt.missingFields.includes("enterprise_sso")) {
    return {
      kind: "enterprise_sso",
      emailAddress: attempt.emailAddress,
    }
  }

  if (
    !attempt.emailAddress &&
    attempt.missingFields.includes("email_address")
  ) {
    return { kind: "collect_email" }
  }

  if (
    attempt.emailAddress &&
    attempt.unverifiedFields.includes("email_address")
  ) {
    return { kind: "verify_email", emailAddress: attempt.emailAddress }
  }

  return { kind: "unsupported" }
}

export function getInvitationSignUpDiagnostics(
  attempt: InvitationSignUpAttempt,
) {
  return {
    status: attempt.status ?? "unknown",
    requiredFields: [...attempt.requiredFields],
    missingFields: [...attempt.missingFields],
    unverifiedFields: [...attempt.unverifiedFields],
    hasCreatedSession: Boolean(attempt.createdSessionId),
    hasEmailAddress: Boolean(attempt.emailAddress),
  }
}
